import { createFileRoute } from "@tanstack/react-router";
import { Check, Mail, MessageCircle } from "lucide-react";

import { BrandLogo } from "@/components/brand/BrandLogo";
import { AccessPortal } from "@/components/landing/AccessPortal";
import { ReviewAccess } from "@/components/landing/ReviewAccess";
import { Button } from "@/components/ui/button";
import { COMPANY, COMPANY_LEGAL_FOOTER, whatsappUrl } from "@/lib/company";
import { PACK_NAME, PACK_START_IDS, type PackStartId } from "@/lib/listing-pack";
import { AUDIT_OFFER, OFFERS, type Offer } from "@/lib/pricing";

const TITLE = "One step left | Adkiro";

interface Step {
  label: string;
  note: string;
  done: boolean;
}

// The first step has to be true on both roads here: after a free review
// agreed in writing, and straight from a pay button, where nothing was
// agreed first.
const PAID_STEPS: Step[] = [
  { label: "Price", note: "Fixed in advance", done: true },
  { label: "Payment", note: "Receipt sent by Stripe", done: true },
];

/**
 * What the client still has to do, by service. The review needs view access
 * only; everything Adkiro runs also needs the photos or renders to advertise.
 */
const FLOWS: Record<Offer["id"], { photos: boolean; heading: string; intro: string; last: Step }> =
  {
    review: {
      photos: false,
      heading: "One step left: let Adkiro see your ad account",
      intro:
        "If you have just paid, Stripe has emailed your receipt. The written review, 90-day roadmap and handover arrive by email within three business days of the access being granted. It is view only: Adkiro can read your ads and results and cannot change anything.",
      // No minute count: the steps are short, but they need a computer and
      // the firm's admin, and nobody has timed a client doing them yet.
      last: { label: "View access", note: "A few minutes, on a computer", done: false },
    },
    launch: {
      photos: true,
      heading: "Last step: access to the ad account and the photos",
      intro:
        "If you have just paid, Stripe has emailed your receipt. Give Adkiro access to manage campaigns on your ad account and Page, and send the photos or renders. Kristian confirms the launch plan by email once both have arrived.",
      last: { label: "Access and photos", note: "About ten minutes", done: false },
    },
    agency: {
      photos: true,
      heading: "Last step: access to the ad account",
      intro:
        "Give Adkiro access to manage campaigns on your ad account and Page, and send the photos of the listings to advertise first. Kristian confirms the launch plan by email once both have arrived.",
      last: { label: "Access and photos", note: "About ten minutes", done: false },
    },
    developer: {
      photos: true,
      heading: "Last step: access to the ad account",
      intro:
        "Give Adkiro access to manage campaigns on your ad account and Page, and send the project renders, plans and photos. Kristian confirms the launch plan by email once both have arrived.",
      last: { label: "Access and material", note: "About ten minutes", done: false },
    },
    portfolio: {
      photos: true,
      heading: "Last step: access to the ad accounts",
      intro:
        "Give Adkiro access to manage campaigns on each ad account and Page in the proposal, and send the renders, plans and photos for the first projects. Kristian confirms the launch calendar by email once they have arrived.",
      last: { label: "Access and material", note: "About fifteen minutes", done: false },
    },
  };

const OFFER_IDS = OFFERS.map((offer) => offer.id);

const PHOTO_CHECKLIST = [
  "Photos or renders, as large as you have them: ten to twenty is ideal",
  "Price, size, bedrooms and the location",
  "Anything the ad must say, or must not",
];

/**
 * A Listing Creative Pack needs no access to anything: only the listing. Its
 * payment links land here like the others, and without this the page would
 * fall back to the review and ask a client who bought four ads for access
 * to an ad account.
 */
const PACK_FLOWS: Record<PackStartId, { name: string; heading: string; intro: string }> = {
  pack: {
    name: PACK_NAME,
    heading: "Last step: send the listing",
    intro:
      "If you have just paid, Stripe has emailed your receipt. Send the photos and the facts of the listing. Kristian confirms the delivery date in writing within one business day. The three working days start when the photos arrive, and the four ads, the two videos and the texts come back as one download link.",
  },
  pack5: {
    name: `${PACK_NAME}: five listings`,
    heading: "Last step: send the first listing",
    intro:
      "If you have just paid, Stripe has emailed your receipt. Send the photos and the facts of the first listing; the others can follow when they are ready, within 60 days. The listings are done one at a time, one every two working days.",
  },
};

const PACK_STEPS: Step[] = [
  ...PAID_STEPS,
  { label: "Photos and facts", note: "About ten minutes", done: false },
];

const PACK_CHECKLIST = [
  "At least eight photos, as large as you have them",
  "The price, the size, the rooms and the floor",
  "Your logo and how buyers should reach you",
  "For a Dubai listing: the permit number and its QR code",
];

// Every Stripe payment link redirects here, with ?service= naming what was
// bought, so the client goes straight from paying to the one thing left to do.
// Without the parameter it shows the review, whose link predates it.
// Unlisted: no nav link, noindex.
export const Route = createFileRoute("/start")({
  validateSearch: (search: Record<string, unknown>): { service?: Offer["id"] | PackStartId } => {
    const service = [...OFFER_IDS, ...PACK_START_IDS].find((id) => id === search["service"]);
    return service ? { service } : {};
  },
  head: () => ({
    meta: [
      { title: TITLE },
      { name: "robots", content: "noindex, nofollow" },
      {
        name: "description",
        content: "The last step after paying for an Adkiro service.",
      },
    ],
  }),
  component: StartPage,
});

function PhotosCard({
  name,
  title = "Send the photos or renders",
  checklist = PHOTO_CHECKLIST,
}: {
  name: string;
  title?: string;
  checklist?: string[];
}) {
  return (
    <div className="glass rounded-3xl p-5 sm:p-8">
      <h2 className="text-xl font-semibold">{title}</h2>
      <ul className="mt-4 space-y-2.5">
        {checklist.map((item) => (
          <li key={item} className="flex items-start gap-2 text-sm text-muted-foreground">
            <Check className="mt-0.5 size-4 shrink-0 text-primary" /> {item}
          </li>
        ))}
      </ul>
      <div className="mt-6 flex flex-col gap-3 sm:flex-row">
        <Button variant="brand" size="lg" asChild>
          <a
            href={whatsappUrl(`Hello Kristian, I've paid for ${name}. Here are the photos.`)}
            target="_blank"
            rel="noopener noreferrer"
          >
            <MessageCircle className="size-4" /> Send on WhatsApp
          </a>
        </Button>
        <Button variant="outline" size="lg" asChild>
          <a href={`mailto:${COMPANY.email}?subject=${encodeURIComponent(`Photos: ${name}`)}`}>
            <Mail className="size-4" /> Send by email
          </a>
        </Button>
      </div>
      <p className="mt-4 text-xs leading-relaxed text-muted-foreground">
        On WhatsApp, attach the photos as documents so they are not compressed. By email, a Google
        Drive or WeTransfer link works too.
      </p>
    </div>
  );
}

function StartPage() {
  const { service } = Route.useSearch();
  const pack = PACK_START_IDS.find((id) => id === service);
  const offer = OFFERS.find((o) => o.id === service) ?? AUDIT_OFFER;
  const flow = FLOWS[offer.id];
  const name = pack ? PACK_FLOWS[pack].name : offer.name;
  const heading = pack ? PACK_FLOWS[pack].heading : flow.heading;
  const intro = pack ? PACK_FLOWS[pack].intro : flow.intro;
  const steps = pack ? PACK_STEPS : [...PAID_STEPS, flow.last];
  // Everyone on this page has already paid. The site-wide WhatsApp link
  // opens as a first enquiry, which is the wrong first sentence here.
  const stuckUrl = whatsappUrl(
    `Hello Kristian, I have paid (${name}) and I am stuck on the last step. A screenshot follows.`,
  );

  return (
    <div className="min-h-screen bg-background">
      <header className="sticky top-0 z-40 border-b border-border bg-background/85 backdrop-blur-xl">
        <div className="mx-auto flex max-w-7xl items-center justify-between px-5 py-4 lg:px-8">
          <BrandLogo />
          <Button variant="outline" size="sm" asChild>
            <a href={stuckUrl} target="_blank" rel="noopener noreferrer">
              Message on WhatsApp
            </a>
          </Button>
        </div>
      </header>

      <main>
        <section className="border-b border-border">
          <div className="mx-auto max-w-3xl px-5 pb-12 pt-14 lg:px-8 lg:pt-20">
            <div className="hero-eyebrow">
              <span className="hero-eyebrow__line" />
              <span>{name}</span>
            </div>
            <h1 className="mt-7 text-[clamp(2.1rem,4.6vw,3.2rem)] leading-[1.04]">{heading}</h1>
            <p className="mt-6 text-lg leading-relaxed text-muted-foreground">{intro}</p>

            <ol className="mt-10 grid gap-3 sm:grid-cols-3">
              {steps.map((step, i) => (
                <li
                  key={step.label}
                  className={`flex items-center gap-3 border p-4 ${
                    step.done ? "border-border bg-card" : "border-primary bg-background"
                  }`}
                >
                  <span
                    className={`flex size-8 shrink-0 items-center justify-center rounded-full text-sm font-semibold ${
                      step.done
                        ? "bg-primary/10 text-primary"
                        : "bg-primary text-primary-foreground"
                    }`}
                    aria-hidden="true"
                  >
                    {step.done ? <Check className="size-4" /> : i + 1}
                  </span>
                  <span>
                    <span className="block font-medium">
                      {step.label}
                      <span className="sr-only">{step.done ? " — done" : " — next"}</span>
                    </span>
                    <span className="block text-xs text-muted-foreground">{step.note}</span>
                  </span>
                </li>
              ))}
            </ol>
          </div>
        </section>

        <section className="mx-auto grid max-w-4xl gap-8 px-5 py-14 lg:px-8">
          {pack ? (
            <PhotosCard
              name={name}
              title="Send the photos and the facts"
              checklist={PACK_CHECKLIST}
            />
          ) : (
            <>
              {flow.photos && <PhotosCard name={name} />}
              {offer.id === "review" ? <ReviewAccess /> : <AccessPortal />}
            </>
          )}
          <p className="text-center text-sm text-muted-foreground">
            Stuck?{" "}
            <a
              href={stuckUrl}
              target="_blank"
              rel="noopener noreferrer"
              className="text-primary hover:text-foreground"
            >
              Send Kristian a screenshot on WhatsApp
            </a>{" "}
            or email{" "}
            <a href={`mailto:${COMPANY.email}`} className="text-primary hover:text-foreground">
              {COMPANY.email}
            </a>
            .
          </p>
        </section>
      </main>

      <footer className="border-t border-border py-8">
        <div className="mx-auto flex max-w-7xl flex-col items-center justify-between gap-3 px-5 text-sm text-muted-foreground sm:flex-row lg:px-8">
          <BrandLogo compact />
          <span className="text-center sm:text-right">
            © {new Date().getFullYear()} Adkiro · {COMPANY_LEGAL_FOOTER}, Sofia ·{" "}
            <a href="/privacy" className="hover:text-foreground">
              Privacy
            </a>{" "}
            ·{" "}
            <a href="/terms#legal-notice" className="hover:text-foreground">
              Legal notice
            </a>
          </span>
        </div>
      </footer>
    </div>
  );
}
